import React, { useState, useMemo, useEffect } from "react";
import { Input } from "src/components/atoms/Input";
import { Select } from "src/components/atoms/Select";
import { Button } from "src/components/atoms/Button";
import closeIcon from "src/assets/icons/close.svg";
// import plusIcon from "src/assets/icons/plusBlue.svg";
// import SearchIcon from "src/assets/icons/search.svg";
import styles from "./styles.module.css";
import { useCategories } from "src/hooks/useCategories";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { fetchClientAccounts } from "src/store/slices/clientDetailSlice";
import { apiClient } from "src/api/client";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";
import { RuleCondition, RuleItem } from "src/api/types";
import { normalizeRole } from "src/constants/roles";
import { DeleteConfirmModal } from "src/components/organisms/DeleteConfirmModal";

// Префилл для "Create rule for <merchant>" (после BulkActions.applyCategory) —
// используется только в create-режиме, initialRule всегда важнее.
type CreateRuleModalPrefill = {
  keyword: string;
  direction: "in" | "out";
  categoryId: number;
  accountId?: number | null;
};

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
  mode?: "create" | "edit";
  initialRule?: RuleItem | null;
  prefill?: CreateRuleModalPrefill | null;
};

// Минимальный priority клиентского правила — см. MIN_CLIENT_PRIORITY в
// backend/main/domains/rule/services.py: create/update отклоняют всё, что меньше.
const MIN_CLIENT_PRIORITY = 1;
const MAX_PRIORITY = 100;

const moneyOptions = [
  { id: "out", label: "Money out" },
  { id: "in", label: "Money in" },
];


// Полный набор операций по description, которые бэкенд принимает от клиентских
// правил (FIELD_OPERATIONS["description"] минус regex — он разрешён только
// правилам с template_key). Держим их все, иначе клон шаблона с equals/endswith
// невозможно сохранить, а UncategorizedGroupsModal создаёт правила, которые
// этот редактор потом отказывается открывать на запись.
export const descriptionOptions = [
  { id: "word", label: "Word" },
  { id: "contains", label: "Contains" },
  { id: "equals", label: "Equals" },
  { id: "startswith", label: "Starts with" },
  { id: "endswith", label: "Ends with" },
];

// Значения — BankAccountType бэкенда (checking/saving/credit),
// см. backend/main/domains/accounts/types.py.
const accountTypeOptions = [
  { id: "any", label: "Any" },
  { id: "checking", label: "Checking" },
  { id: "saving", label: "Saving" },
  { id: "credit", label: "Credit" },
];

// Неизвестное (пришедшее из шаблона) значение показываем отдельным пунктом,
// чтобы Select не остался пустым.
const withUnknownOption = <T extends { id: string | number; label: string }>(
  options: T[],
  selectedId: string | number | null,
) =>
  typeof selectedId === "string" && !options.some((option) => option.id === selectedId)
    ? [...options, { id: selectedId, label: selectedId }]
    : options;

const getAmountCondition = (conditions: RuleCondition[] | undefined) =>
  conditions?.find((condition) => condition.name === "amount");

const getAmountDirection = (conditions: RuleCondition[] | undefined) => {
  const amountCondition = getAmountCondition(conditions);
  if (!amountCondition) return "out";
  return ["gte", "gt"].includes(amountCondition.type_operation) ? "in" : "out";
};

const getDescriptionCondition = (conditions: RuleCondition[] | undefined) =>
  conditions?.find(
    (condition) => condition.name === "description" && condition.type === "string",
  );

const getAccountTypeCondition = (conditions: RuleCondition[] | undefined) =>
  conditions?.find((condition) => condition.name === "account_type");

// Условия, которыми модалка не управляет напрямую (account_kind шаблона, второе
// условие по description, amount-порог сверх первого) — сохраняем как есть.
// Отбрасываем ровно те объекты, которые форма реально загрузила в свои поля:
// фильтр по имени терял бы всё, что getters не подхватили (дубли description,
// account_type с операцией не equals).
const getOtherConditions = (conditions: RuleCondition[] | undefined): RuleCondition[] => {
  const managed = new Set<RuleCondition>(
    [
      getAmountCondition(conditions),
      getDescriptionCondition(conditions),
      getAccountTypeCondition(conditions),
    ].filter((condition): condition is RuleCondition => Boolean(condition)),
  );
  return (conditions ?? []).filter((condition) => !managed.has(condition));
};

export const CreateRuleModal: React.FC<Props> = ({
  onClose,
  onSuccess,
  mode = "create",
  initialRule = null,
  prefill = null,
}) => {
  // const [step, setStep] = useState<1 | 2>(1);
  const isEditMode = mode === "edit" && !!initialRule;
  const isTemplateRule = isEditMode && !!initialRule?.template_key;
  const descriptionCondition = getDescriptionCondition(initialRule?.conditions_json);
  const accountTypeCondition = getAccountTypeCondition(initialRule?.conditions_json);
  const amountCondition = getAmountCondition(initialRule?.conditions_json);

  const [ruleName, setRuleName] = useState(initialRule?.name ?? prefill?.keyword ?? "");
  const [selectedMoney, setSelectedMoney] = useState<string | number | null>(
    initialRule ? getAmountDirection(initialRule.conditions_json) : prefill?.direction ?? "out",
  );
  const [selectedAccounts, setSelectedAccounts] = useState<
    string | number | null
  >(initialRule?.account_id ?? prefill?.accountId ?? "");
  const [selectedDescription, setSelectedDescription] = useState<
    string | number | null
  >(descriptionCondition?.type_operation ?? "word");
  const [descriptionText, setDescriptionText] = useState(
    descriptionCondition?.value ? String(descriptionCondition.value) : prefill?.keyword ?? "",
  );
  const [selectedAccountType, setSelectedAccountType] = useState<string | number | null>(
    accountTypeCondition?.value ? String(accountTypeCondition.value) : "any",
  );
  // priority ≤ 0 — валидное значение для шаблонов (общие −1, отраслевые 0,
  // fallback −2), поэтому проверяем на null/undefined, а не на truthiness:
  // иначе поле само подставляло бы 1 и показывало чужой приоритет. Шаблоны
  // read-only, а при Clone handleStartClone подтягивает значение до 1.
  const [priority, setPriority] = useState(
    initialRule ? String(initialRule.priority ?? MIN_CLIENT_PRIORITY) : String(MIN_CLIENT_PRIORITY),
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    number | string | null
  >(initialRule?.category_id ?? prefill?.categoryId ?? null);
  const [isCloning, setIsCloning] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  // const [searchQuery, setSearchQuery] = useState("");
  // const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReapplying, setIsReapplying] = useState(false);
  const [savedRuleId, setSavedRuleId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isReadOnly = isTemplateRule && !isCloning;
  // Клон шаблона и обычное создание уходят в POST, правка существующего — в PATCH.
  const shouldCreate = !isEditMode || isCloning;

  // Условия, унаследованные из исходного правила и не редактируемые этой модалкой
  // (например, account_kind шаблона) — переносим как есть.
  const otherConditions = useMemo(
    () => getOtherConditions(initialRule?.conditions_json),
    [initialRule],
  );

  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories();

  const dispatch = useAppDispatch();
  const selectedClient = useAppSelector((state) => state.selectedClient.client);
  const authUser = useAppSelector((state) => state.auth.user);
  const { accounts, accountsForClientId } = useAppSelector(
    (state) => state.clientDetail,
  );
  const userRole = authUser?.type ? normalizeRole(authUser.type) : null;
  const isAccountant = userRole === "accountant";

  const accountsClientId = isAccountant ? selectedClient?.id : authUser?.id;
  // На первом рендере и пока fetchClientAccounts в полёте accountsForClientId
  // равен null (pending-редьюсер обнуляет его при смене клиента), т.е. accounts
  // ещё не относятся к этому клиенту.
  const accountsLoaded = !!accountsClientId && accountsForClientId === accountsClientId;
  const scopedAccounts = useMemo(
    () => (accountsLoaded ? accounts : []),
    [accounts, accountsLoaded],
  );

  useEffect(() => {
    if (isAccountant && !selectedClient) {
      onClose();
    }
  }, [isAccountant, onClose, selectedClient]);

  useEffect(() => {
    const nextDescriptionCondition = getDescriptionCondition(initialRule?.conditions_json);
    const nextAccountTypeCondition = getAccountTypeCondition(initialRule?.conditions_json);
    setRuleName(initialRule?.name ?? prefill?.keyword ?? "");
    setSelectedMoney(
      initialRule ? getAmountDirection(initialRule.conditions_json) : prefill?.direction ?? "out",
    );
    setSelectedAccounts(initialRule?.account_id ?? prefill?.accountId ?? "");
    setSelectedDescription(nextDescriptionCondition?.type_operation ?? "word");
    setDescriptionText(
      nextDescriptionCondition?.value
        ? String(nextDescriptionCondition.value)
        : prefill?.keyword ?? "",
    );
    setSelectedAccountType(
      nextAccountTypeCondition?.value ? String(nextAccountTypeCondition.value) : "any",
    );
    setPriority(
      initialRule
        ? String(initialRule.priority ?? MIN_CLIENT_PRIORITY)
        : String(MIN_CLIENT_PRIORITY),
    );
    setSelectedCategoryId(initialRule?.category_id ?? prefill?.categoryId ?? null);
    setIsCloning(false);
    setError(null);
  }, [initialRule, prefill]);

  useEffect(() => {
    if (accountsClientId) {
      dispatch(fetchClientAccounts({ client_id: accountsClientId, page_size: 100 }));
    }
  }, [dispatch, accountsClientId]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId],
  );

  const filteredAccounts = useMemo(() => {
    if (!selectedCategoryId || !selectedCategory) return scopedAccounts;
    const industryId = selectedCategory.industry_id;
    if (!industryId) return scopedAccounts;
    return scopedAccounts.filter((acc) => acc.company?.industry_id === industryId);
  }, [scopedAccounts, selectedCategory, selectedCategoryId]);

  useEffect(() => {
    // Пока счета клиента не загружены, filteredAccounts пуст — сбрасывать по нему
    // нельзя: иначе account_id из prefill/initialRule молча превращается в
    // "All bank accounts" и Save расширяет существующее правило на все счета.
    if (!selectedAccounts || !accountsLoaded) return;
    const stillValid = filteredAccounts.some((acc) => acc.id === selectedAccounts);
    if (!stillValid) setSelectedAccounts("");
  }, [accountsLoaded, filteredAccounts, selectedAccounts]);

  const accountOptions = useMemo(() => [
    { id: "", label: "All bank accounts" },
    ...filteredAccounts.map((acc) => ({ id: acc.id, label: acc.account_name || `Account ${acc.id}` })),
  ], [filteredAccounts]);

  // Опции для Select
  const categoryOptions = useMemo(
    () =>
      categories.map((cat) => ({
        id: cat.id || 0,
        label: cat.name,
      })),
    [categories],
  );

  // Шаблонные правила иногда используют операции вне UI (regex/equals) — не выбрасываем,
  // а показываем их как есть, чтобы Select не остался пустым.
  const effectiveDescriptionOptions = withUnknownOption(
    descriptionOptions,
    selectedDescription,
  );
  const effectiveAccountTypeOptions = withUnknownOption(
    accountTypeOptions,
    selectedAccountType,
  );

  // // Группировка по parent_id для кастомного списка в Step 2
  // const rootCategories = useMemo(
  //   () => categories.filter((c) => c.parent_id === null),
  //   [categories],
  // );

  // const getSubcategories = (parentId: number) =>
  //   categories.filter((c) => c.parent_id === parentId);

  // const filteredCategories = useMemo(() => {
  //   if (!searchQuery.trim()) return rootCategories;
  //   const q = searchQuery.toLowerCase();
  //   return categories.filter((c) => c.name.toLowerCase().includes(q));
  // }, [categories, rootCategories, searchQuery]);

  // const handleContinue = () => {
  //   setStep(2);
  // };

  // Клон уходит в POST /rules, а бэкенд (rule/services.create) отклоняет
  // priority < 1. У всех шаблонов priority ≤ 0 (общие −1, отраслевые 0,
  // fallback −2), поэтому без подтягивания к 1 клон открывался бы сразу
  // невалидным: превью молчит, а "Create cloned rule" ругается "1-100" —
  // и поле числовое, поправить "-1" дописыванием нельзя.
  const handleStartClone = () => {
    setIsCloning(true);
    if (!priority || Number(priority) < MIN_CLIENT_PRIORITY) {
      setPriority(String(MIN_CLIENT_PRIORITY));
    }
  };

  const keyword = descriptionText.trim();
  // Правило без keyword допустимо только если исходное правило тоже было без него
  // (amount-only правила из админки и fallback-шаблон) — иначе модалка навсегда
  // запирала бы их и от Save, и от Clone.
  const allowEmptyKeyword = isEditMode && !descriptionCondition;
  const priorityNum = Number(priority);

  // Одна проверка на submit и на превью: разъехавшись, превью обещало бы
  // "Will match N transactions" для payload, который Save потом отвергает.
  const payloadError = useMemo(() => {
    if (!keyword ? !allowEmptyKeyword : keyword.length < 2) {
      return "Keyword must be at least 2 characters";
    }
    // regex разрешён бэкендом только правилам с template_key, а клон уходит в POST
    // без него — ловим здесь, иначе пользователь получает сырой 422 ровно в том
    // сценарии, ради которого клон и сделан.
    if (
      keyword &&
      shouldCreate &&
      !descriptionOptions.some((option) => option.id === selectedDescription)
    ) {
      return "This rule uses a match type this editor cannot create. Pick one of the listed match types.";
    }
    if (!priority || priorityNum < MIN_CLIENT_PRIORITY || priorityNum > MAX_PRIORITY) {
      return `Please enter priority (${MIN_CLIENT_PRIORITY}-${MAX_PRIORITY})`;
    }
    return null;
  }, [allowEmptyKeyword, keyword, priority, priorityNum, selectedDescription, shouldCreate]);

  // Общая сборка conditions_json для submit и превью — чтобы у них не разъехалась логика.
  const buildConditions = (keyword: string): RuleCondition[] => {
    const keepsAmountThreshold =
      selectedMoney === getAmountDirection(initialRule?.conditions_json);

    const conditions: RuleCondition[] = [
      amountCondition && keepsAmountThreshold
        ? { ...amountCondition }
        : {
            type: "float",
            type_operation: selectedMoney === "out" ? "lte" : "gte",
            name: "amount",
            value: "0",
          },
    ];

    if (keyword) {
      conditions.push({
        type: "string",
        type_operation: selectedDescription as string, // см. descriptionOptions
        name: "description",
        value: keyword,
      });
    }

    if (selectedAccountType && selectedAccountType !== "any") {
      conditions.push({
        type: "string",
        // "equals" — имя операции в контракте API; "exact" это Django-lookup,
        // бэкенд такую операцию не принимает.
        type_operation: "equals",
        name: "account_type",
        value: selectedAccountType as string,
      });
    }

    conditions.push(...otherConditions);
    return conditions;
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    setError(null);

    if (!accountsClientId) {
      setError(isAccountant ? "Please select a client" : "User not found");
      return;
    }

    // Валидация
    if (!ruleName.trim()) {
      setError("Please enter a rule name");
      return;
    }

    if (!selectedCategoryId) {
      setError("Please select a category");
      return;
    }

    if (payloadError) {
      setError(payloadError);
      return;
    }

    try {
      setIsSubmitting(true);

      const conditions = buildConditions(keyword);

      const payload = {
        name: ruleName,
        account_id: selectedAccounts ? Number(selectedAccounts) : null, // null = all accounts
        category_id: Number(selectedCategoryId),
        conditions,
        priority: priorityNum,
      };

      const savedRule = !shouldCreate && initialRule
        ? await apiClient.updateRule(initialRule.id, payload)
        : await apiClient.createRule(payload);

      setSavedRuleId(savedRule.id);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Превью «совпадёт с N существующими» — тот же payload, что уходит на submit,
  // с дебаунсом; молчим при ошибке (превью необязательно для сохранения).
  useEffect(() => {
    if (isReadOnly || !selectedCategoryId || payloadError) {
      setPreviewCount(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const { count } = await apiClient.previewRule({
          name: ruleName || "preview",
          account_id: selectedAccounts ? Number(selectedAccounts) : null,
          category_id: Number(selectedCategoryId),
          conditions: buildConditions(keyword),
          priority: priorityNum,
        });
        if (!cancelled) setPreviewCount(count);
      } catch {
        if (!cancelled) setPreviewCount(null);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isReadOnly,
    payloadError,
    selectedCategoryId,
    selectedMoney,
    selectedAccounts,
    selectedDescription,
    // keyword, а не descriptionText: в payload уходит trim(), и лишний пробел
    // не должен стоить ещё одного COUNT по всем неразобранным строкам клиента.
    keyword,
    selectedAccountType,
    priority,
    otherConditions,
  ]);

  const finishWithoutReapply = () => {
    setSavedRuleId(null);
    onSuccess?.();
    onClose();
  };

  const handleReapply = async () => {
    if (!savedRuleId) return;
    try {
      setIsReapplying(true);
      setError(null);
      await apiClient.reapplyRule(savedRuleId);
      window.dispatchEvent(new Event("ruleReapplyStarted"));
      setSavedRuleId(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      setSavedRuleId(null);
      setError(getErrorMessage(err));
    } finally {
      setIsReapplying(false);
    }
  };

  // const toggleCategory = (categoryId: number) => {
  //   if (expandedCategories.includes(categoryId)) {
  //     setExpandedCategories(expandedCategories.filter((id) => id !== categoryId));
  //   } else {
  //     setExpandedCategories([...expandedCategories, categoryId]);
  //   }
  // };

  if (!accountsClientId) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose}></div>
      <div className={styles.modal}>
          <div className={styles.header}>
            <h2 className={styles.title}>
              {isCloning ? "Clone rule" : isTemplateRule ? "Template rule" : isEditMode ? "Edit rule" : "Create rule"}
              {isTemplateRule && !isCloning && (
                <span className={styles.templateBadge}>Template</span>
              )}
              {isEditMode && initialRule?.disabled && (
                <span className={`${styles.templateBadge} ${styles.disabledBadge}`}>
                  Disabled
                </span>
              )}
            </h2>
            <button className={styles.closeBtn} onClick={onClose}>
              <img src={closeIcon} alt="close" />
            </button>
          </div>

          <div className={styles.content}>
            <div className={styles.field}>
              <label className={styles.label}>
                What do you want to call this rule?
              </label>
              <Input
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                className={styles.input}
                placeholder="Name this rule"
                disabled={isReadOnly}
              />
            </div>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                Apply this to transactions that are
              </h3>
              <div className={styles.ruleRow}>
                <Select
                  items={moneyOptions}
                  selectedId={selectedMoney}
                  onSelect={setSelectedMoney}
                  placeholder="Money out"
                  className={styles.ruleSelect}
                  selectedClassName={styles.ruleSelectButton}
                  menuClassName={styles.ruleSelectMenu}
                  disabled={isReadOnly}
                />
                <span className={styles.ruleText}>in</span>
                <Select
                  items={accountOptions}
                  selectedId={selectedAccounts}
                  onSelect={setSelectedAccounts}
                  placeholder="All bank accounts"
                  className={styles.ruleSelect}
                  selectedClassName={styles.ruleSelectButton}
                  menuClassName={styles.ruleSelectMenu}
                  disabled={isReadOnly}
                />
                <span className={styles.ruleText}>if description</span>
                <Select
                  items={effectiveDescriptionOptions}
                  selectedId={selectedDescription}
                  onSelect={setSelectedDescription}
                  placeholder="Word"
                  className={styles.ruleSelectSmall}
                  selectedClassName={styles.ruleSelectButton}
                  menuClassName={styles.ruleSelectMenu}
                  disabled={isReadOnly}
                />
              </div>
              <div className={styles.field}>
                <textarea
                  value={descriptionText}
                  onChange={(e) => setDescriptionText(e.target.value)}
                  placeholder="Zelle payment to Highlander 2708 IL JPM99be7v1hf"
                  className={styles.descriptionTextarea}
                  disabled={isReadOnly}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Account type</label>
                <Select
                  items={effectiveAccountTypeOptions}
                  selectedId={selectedAccountType}
                  onSelect={setSelectedAccountType}
                  placeholder="Any"
                  className={styles.select}
                  disabled={isReadOnly}
                />
              </div>
              {/* <button className={styles.addCondition}>
                <img src={plusIcon} alt="add" />
                Add a condition
              </button> */}
            </div>
            <div className={styles.section}>
              <ErrorBlock error={categoriesError || error} />
              <div className={styles.field}>
                <label className={styles.label}>Category</label>
                <Select
                  items={categoryOptions}
                  selectedId={selectedCategoryId}
                  onSelect={setSelectedCategoryId}
                  placeholder={
                    categoriesLoading ? "Loading categories..." : "Select a category"
                  }
                  className={styles.select}
                  disabled={categoriesLoading || isReadOnly}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  Enter the priority of the rule
                </label>
                <Input
                  value={priority}
                  className={styles.input}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, "");
                    if (raw === "") {
                      setPriority("");
                      return;
                    }
                    const num = Math.min(
                      MAX_PRIORITY,
                      Math.max(MIN_CLIENT_PRIORITY, parseInt(raw, 10)),
                    );
                    setPriority(String(num));
                  }}
                  placeholder={`${MIN_CLIENT_PRIORITY}-${MAX_PRIORITY}`}
                  disabled={isReadOnly}
                />
                <span className={styles.hint}>Higher priority wins</span>
                {previewCount !== null && (
                  <span className={styles.hint}>
                    Will match {previewCount} existing transaction{previewCount === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            {isReadOnly ? (
              <Button text="Clone as my rule" onClick={handleStartClone} />
            ) : (
              <Button
                text={
                  isSubmitting
                    ? shouldCreate
                      ? "Creating..."
                      : "Saving..."
                    : isCloning
                      ? "Create cloned rule"
                      : isEditMode
                        ? "Save rule"
                        : "Create new rule"
                }
                onClick={handleSubmit}
                disabled={isSubmitting}
              />
            )}
          </div>
        </div>
      {savedRuleId && (
        <DeleteConfirmModal
          title="Apply rule to existing transactions?"
          message="The update runs in the background. Manual categories are preserved."
          confirmText="Apply rule"
          cancelText="Not now"
          loadingText="Applying..."
          isDeleting={isReapplying}
          onCancel={finishWithoutReapply}
          onConfirm={handleReapply}
        />
      )}
      {/* {step === 2 && (
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2 className={styles.title}>Create rule</h2>
            <button className={styles.closeBtn} onClick={onClose}>
              <img src={closeIcon} alt="close" />
            </button>
          </div>
          <div className={styles.content}>
            <div className={styles.field}>
              <label className={styles.label}>
                What do you want to call this rule?
              </label>
              <div className={styles.suggested}>SUGGESTED</div>
              <div className={styles.categoryPreview}>
                <div className={styles.categoryItem}>
                  <span className={styles.categoryName}>
                    Bank fees & service charges
                  </span>
                  <span className={styles.categoryParent}>
                    Sub of Car Rental Ex...
                  </span>
                </div>
                <button className={styles.addNewCategory}>
                  <img src={plusIcon} alt="add" />
                  Add new account
                </button>
              </div>
            </div>
            <div className={styles.searchWrapper}>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search category name"
                leftIcon={SearchIcon}
                className={styles.searchInput}
              />
            </div>
            <div className={styles.categoriesList}>
              {filteredCategories.map((category) => {
                const subcategories = getSubcategories(category.id || 0);
                return (
                  <div key={category.id} className={styles.categoryBlock}>
                    <div
                      className={styles.categoryHeader}
                      onClick={() => toggleCategory(category.id || 0)}
                    >
                      <span>{category.name}</span>
                    </div>
                    {expandedCategories.includes(category.id || 0) &&
                      subcategories.length > 0 && (
                        <div className={styles.subcategories}>
                          {subcategories.map((sub) => (
                            <div
                              key={sub.id}
                              className={styles.subcategoryItem}
                              onClick={() => setSelectedCategoryId(sub.id || null)}
                            >
                              <span>{sub.name}</span>
                              <span className={styles.subcategoryParent}>
                                {category.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
            <div className={styles.categorySelectWrapper}>
              <Select
                items={categoryOptions}
                selectedId={selectedCategoryId}
                onSelect={setSelectedCategoryId}
                placeholder={
                  categoriesLoading ? "Loading categories..." : "Select a category"
                }
                className={styles.select}
                disabled={categoriesLoading}
              />
            </div>
          </div>
          <div className={styles.footer}>
            <Button
              text={isSubmitting ? "Creating..." : "Create rule"}
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
          </div>
        </div>
      )} */}
    </>
  );
};
